import { useEffect, useRef, useState, ReactNode } from "react";
import type { Subject, SubjectIcon as SubjectIconName } from "../lib/types";
import { SUBJECT_DISCOUNT_HINTS } from "../lib/discounts";
import { useCart } from "../lib/cartStore";
import { useSubscriptions } from "../lib/subscriptionContext";

interface SubjectSectionProps {
  subject: Subject;
  /** Product group ids shown in this section — used to open it when it holds a cart item or owned product. */
  productIds?: string[];
  children: ReactNode;
}

const ICON_PATHS: Record<SubjectIconName, ReactNode> = {
  calculator: (
    <>
      <rect x="5" y="2" width="14" height="20" rx="2" />
      <path d="M8 6h8" />
      <path d="M8 11h.01M12 11h.01M16 11h.01M8 15h.01M12 15h.01M16 15h.01M8 19h.01M12 19h.01M16 15v4" />
    </>
  ),
  "bar-chart": (
    <>
      <path d="M3 3v18h18" />
      <path d="M8 17v-5M13 17V8M18 17V5" />
    </>
  ),
  laptop: (
    <>
      <rect x="4" y="4" width="16" height="11" rx="2" />
      <path d="M2 19h20" />
    </>
  ),
  flask: (
    <>
      <path d="M9 3h6M10 3v6.5L4.6 18.6A1.6 1.6 0 0 0 6 21h12a1.6 1.6 0 0 0 1.4-2.4L14 9.5V3" />
      <path d="M7 15h10" />
    </>
  ),
};

function SubjectIcon({ icon }: { icon: SubjectIconName }) {
  return (
    <svg
      width="22"
      height="22"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
    >
      {ICON_PATHS[icon]}
    </svg>
  );
}

function TagIcon() {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      xmlns="http://www.w3.org/2000/svg"
      aria-hidden="true"
    >
      <path d="M20.6 13.4l-7.2 7.2a2 2 0 0 1-2.8 0L2 12V2h10l8.6 8.6a2 2 0 0 1 0 2.8z" />
      <path d="M7 7h.01" />
    </svg>
  );
}

export function SubjectSection({ subject, productIds = [], children }: SubjectSectionProps) {
  const { entries } = useCart();
  const { getSubscriptionForProduct } = useSubscriptions();
  const discountHint = SUBJECT_DISCOUNT_HINTS[subject.id];

  // D2: a section holding a cart item or an owned product starts open, even
  // when the cart is pre-filled after mount (quote/reorder links). Once the user
  // toggles the section, their choice wins.
  const holdsSelection = productIds.some(
    (id) => entries.some((entry) => entry.groupId === id) || !!getSubscriptionForProduct(id),
  );
  const [isExpanded, setIsExpanded] = useState(subject.defaultExpanded || holdsSelection);
  const userToggled = useRef(false);

  useEffect(() => {
    if (holdsSelection && !userToggled.current) setIsExpanded(true);
  }, [holdsSelection]);

  const contentId = `subject-content-${subject.id}`;

  return (
    <div className="subject-section" data-subject={subject.id}>
      <button
        type="button"
        className="subject-header"
        onClick={() => {
          userToggled.current = true;
          setIsExpanded(!isExpanded);
        }}
        aria-expanded={isExpanded}
        aria-controls={contentId}
      >
        <span className="subject-icon" aria-hidden="true">
          <SubjectIcon icon={subject.icon} />
        </span>
        <span className="subject-heading">
          <h2 className="subject-title">{subject.name}</h2>
          <span className="subject-tagline">{subject.tagline}</span>
        </span>
        <span className={`subject-chevron ${isExpanded ? "expanded" : ""}`}>
          <svg
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M5 7.5L10 12.5L15 7.5"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </span>
      </button>

      {isExpanded && (
        <div className="subject-content" id={contentId}>
          {children}
          {discountHint && (
            <div className="discount-hint">
              <TagIcon />
              <span>{discountHint}</span>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
